/**
 * Soft opt-in card for browser notifications.
 *
 * We never fire the native permission dialog cold — a denial is permanent
 * for the origin. This card explains the value first and only calls
 * `enablePush` after an explicit tap. Hidden entirely when the browser
 * can't support push, the visitor already decided, or they dismissed it
 * recently.
 */
import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { canShowSoftPrompt, enablePush, rememberSoftPromptDismissed } from "@/lib/push/client";
import { useConsentRecorder } from "@/components/consent/ConsentBlock";
import { trackPushPrompt } from "@/lib/push/telemetry";
import { CONSENT_HEADLINE } from "@/lib/consent/channels";

type Props = {
  /** Where the prompt was triggered — stored for reporting. */
  source: string;
  /** Only render once the moment is right (e.g. quiz finished). */
  active?: boolean;
  title?: string;
  description?: string;
  className?: string;
};

export function PushOptInCard({
  source,
  active = true,
  title = CONSENT_HEADLINE,
  description = "Turn on browser notifications for order updates, restock alerts and your protocol reminders. You can change this any time.",
  className,
}: Props) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const recordConsent = useConsentRecorder(source);

  useEffect(() => {
    if (!active) return;
    // Defer a beat so the prompt never competes with the page's own reveal.
    const t = window.setTimeout(() => setVisible(canShowSoftPrompt()), 1200);
    return () => window.clearTimeout(t);
  }, [active]);

  if (!visible) return null;

  const onEnable = async () => {
    setBusy(true);
    trackPushPrompt("prompted", { source });
    let granted = false;
    const result = await enablePush(source, {
      onPermissionGranted: () => {
        granted = true;
        trackPushPrompt("granted", { source });
      },
      onFailure: (reason) =>
        trackPushPrompt(reason === "blocked_context" || reason === "unsupported_browser" ? "unsupported" : "error", {
          source,
          reason,
        }),
    });
    setBusy(false);
    setVisible(false);
    if (result === "subscribed") {
      void recordConsent({ push: true }, { explicit: true });
      if (!granted) trackPushPrompt("granted", { source });
    } else if (result === "denied") {
      // Browser-level refusal, recorded but not treated as a withdrawal.
      void recordConsent({ push: false }, { explicit: false });
      trackPushPrompt("denied", { source });
    }

    if (result === "subscribed") toast.success("Notifications on for this device.");
    else if (result === "denied") toast("No problem, you can enable it later in browser settings.");
    else if (result === "error") toast.error("Could not turn on notifications. Please try again.");
  };

  const onDismiss = () => {
    trackPushPrompt("dismissed", { source });
    // Dismissing a prompt is not a withdrawal — never let it clear consent.
    void recordConsent({ push: false }, { explicit: false });
    rememberSoftPromptDismissed();
    setVisible(false);
  };

  return (
    <div
      className={
        "relative flex items-start gap-3 rounded-2xl border border-border bg-card/80 p-4 shadow-sm backdrop-blur " +
        (className ?? "")
      }
      role="region"
      aria-label="Enable browser notifications"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand))]">
        <Bell className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={onEnable} disabled={busy}>
            {busy ? "Turning on…" : "Turn on"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification prompt"
        className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground transition hover:bg-muted"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
